import React from 'react';
import { styled } from '@compiled/react';
import { Field as AkField } from '@atlaskit/form';
import { RadioGroup } from '@atlaskit/radio';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import { borderRadius } from '@atlassian/jira-common-styles/src/main.tsx';
import { injectIntlV2 as injectIntl } from '@atlassian/jira-intl/src/v2/inject.tsx';
import {
	NEW_SLA_FORMAT,
	OLD_SLA_FORMAT,
} from '@atlassian/jira-servicedesk-sla-panel/src/common/constants';
import type { SLA_DISPLAY_FORMAT } from '@atlassian/jira-servicedesk-sla-panel/src/common/types';
import SlaLozenge from '@atlassian/jira-servicedesk-sla-panel/src/ui/sla-lozenge';
import type { Intl } from '@atlassian/jira-shared-types/src/general.tsx';
import {
	AtRiskOngoingSlaNewSlaFormat,
	AtRiskOngoingSlaOldSlaFormat,
	CompletedSlaNewSlaFormat,
	CompletedSlaOldSlaFormat,
	OnTrackOngoingSlaNewSlaFormat,
	OnTrackOngoingSlaOldSlaFormat,
} from '../../../../../../common/ui/constants';
import { SlaFieldTransformer } from '../../../../../../common/ui/sla-display-format/utils';
import messages from './messages';

export const SLACapsules = ({ slaDisplayFormat }: { slaDisplayFormat: SLA_DISPLAY_FORMAT }) => {
	let OnTrackOngoingSLA;
	let AtRiskOngoingSLA;
	let CompletedSLA;
	switch (slaDisplayFormat) {
		case NEW_SLA_FORMAT:
			OnTrackOngoingSLA = OnTrackOngoingSlaNewSlaFormat;
			AtRiskOngoingSLA = AtRiskOngoingSlaNewSlaFormat;
			CompletedSLA = CompletedSlaNewSlaFormat;
			break;

		case OLD_SLA_FORMAT:
			OnTrackOngoingSLA = OnTrackOngoingSlaOldSlaFormat;
			AtRiskOngoingSLA = AtRiskOngoingSlaOldSlaFormat;
			CompletedSLA = CompletedSlaOldSlaFormat;
			break;

		default:
			OnTrackOngoingSLA = OnTrackOngoingSlaNewSlaFormat;
			AtRiskOngoingSLA = OnTrackOngoingSlaNewSlaFormat;
			CompletedSLA = CompletedSlaNewSlaFormat;
			break;
	}
	return (
		<SlaCapsuleContainer>
			<SlaCapsule>
				<SlaLozenge
					value={
						// @ts-expect-error - TS2322 - Type '{ readonly isBreached: false; readonly timeRemainingInMinutes: 37; readonly status: "ONGOING"; readonly timeRemainingFriendly: "Today 04:00 PM"; readonly metricName: "Time to Pending"; ... 12 more ...; readonly date: 1647858600000; } | { ...; } | TransformedGoalView' is not assignable to type 'TransformedGoalView'.
						SlaFieldTransformer(OnTrackOngoingSLA)
					}
				/>
			</SlaCapsule>
			<SlaCapsule>
				<SlaLozenge
					value={
						// @ts-expect-error - TS2322 - Type '{ readonly isBreached: false; readonly timeRemainingInMinutes: 37; readonly status: "ONGOING"; readonly timeRemainingFriendly: "Today 04:00 PM"; readonly metricName: "Time to Pending"; ... 12 more ...; readonly date: 1647858600000; } | { ...; } | { ...; } | TransformedGoalView' is not assignable to type 'TransformedGoalView'.
						SlaFieldTransformer(AtRiskOngoingSLA)
					}
				/>
			</SlaCapsule>
			<SlaCapsule>
				<SlaLozenge
					value={
						// @ts-expect-error - TS2322 - Type '{ readonly isBreached: false; readonly timeRemainingInMinutes: 300; readonly status: "COMPLETED"; readonly timeRemainingFriendly: "24 February"; readonly metricName: "Time to Pending"; ... 5 more ...; readonly slaDisplayFormat: "NEW_SLA_FORMAT"; } | { ...; }' is not assignable to type 'TransformedGoalView'.
						SlaFieldTransformer(CompletedSLA)
					}
				/>
			</SlaCapsule>
		</SlaCapsuleContainer>
	);
};
type DisplayFormatOptionProps = {
	slaDisplayFormat: SLA_DISPLAY_FORMAT;
} & Intl;

const SlaDisplayFormatOption = ({
	slaDisplayFormat,
	intl: { formatMessage },
}: DisplayFormatOptionProps) => {
	const formattedSLADisplay: string =
		slaDisplayFormat === NEW_SLA_FORMAT
			? formatMessage(messages.dueDateCentric)
			: formatMessage(messages.timeCentric);
	return (
		<DisplayOptionRowView>
			<DisplayFormatTextConatiner>{formattedSLADisplay}</DisplayFormatTextConatiner>
			<SLACapsules slaDisplayFormat={slaDisplayFormat} />
		</DisplayOptionRowView>
	);
};

const SlaDisplayFormatLabel = injectIntl(SlaDisplayFormatOption);

const options = [
	{
		name: 'slaDisplayFormat',
		value: NEW_SLA_FORMAT,
		label: <SlaDisplayFormatLabel slaDisplayFormat={NEW_SLA_FORMAT} />,
		testId:
			'servicedesk-sla-settings.ui.metrics.metric.editable.form.sla-display-format.new-sla-format',
	},
	{
		name: 'slaDisplayFormat',
		value: OLD_SLA_FORMAT,
		label: <SlaDisplayFormatLabel slaDisplayFormat={OLD_SLA_FORMAT} />,
		testId:
			'servicedesk-sla-settings.ui.metrics.metric.editable.form.sla-display-format.old-sla-format',
	},
];

type Props = {
	slaDisplayFormat: SLA_DISPLAY_FORMAT;
};

export const SlaDisplayFormat = ({ slaDisplayFormat }: Props) => (
	<AkField name="slaDisplayFormat" defaultValue={slaDisplayFormat}>
		{({ fieldProps }) => (
			<SlaDisplaySettingsContainer data-test-id="servicedesk-sla-settings.ui.metrics.metric.editable.form.sla-display-format">
				<RadioGroup
					onChange={fieldProps.onChange}
					options={options}
					defaultValue={slaDisplayFormat}
				/>
			</SlaDisplaySettingsContainer>
		)}
	</AkField>
);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- Ignored via go/DSP-18766
const SlaDisplaySettingsContainer = styled.div({
	marginBottom: token('space.300', '24px'),
	paddingTop: token('space.050', '4px'),
	paddingBottom: token('space.025', '2px'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	borderRadius: `${borderRadius}px`,
	boxShadow: token(
		'elevation.shadow.raised',
		'0 1px 1px rgba(9, 30, 66, 0.25), 0 0 1px 1px rgba(9, 30, 66, 0.13)',
	),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	"input[name='slaDisplayFormat']": {
		marginLeft: token('space.100', '8px'),
		marginTop: token('space.100', '8px'),
		marginRight: token('space.negative.050', '-4px'),
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	background: token('elevation.surface.raised', colors.N0),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- Ignored via go/DSP-18766
const DisplayOptionRowView = styled.div({
	alignItems: 'center',
	display: 'flex',
	flexDirection: 'row',
	justifyContent: 'space-between',
	marginTop: token('space.050', '4px'),
	marginBottom: token('space.050', '4px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- Ignored via go/DSP-18766
const DisplayFormatTextConatiner = styled.div({
	width: '140px',
	marginRight: token('space.050', '4px'),
	overflow: 'hidden',
	textOverflow: 'ellipsis',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- Ignored via go/DSP-18766
const SlaCapsuleContainer = styled.div({
	display: 'flex',
	flexWrap: 'wrap',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-exported-styles, @atlaskit/ui-styling-standard/no-styled -- Ignored via go/DSP-18766
export const SlaCapsule = styled.div({
	display: 'flex',
	width: '163.75px',
	marginRight: token('space.050', '4px'),
});
